import React from "react";
import {
  getLocationPlans,
  FamilyPlanOptions,
  BusinessPlanOptions,
  toCurrency,
  LocalProjectAddon,
  calculateFamilyPlanCost,
  calculateBusinessPlanCost,
  Location,
  pluralise,
} from "@ecologi/shared";
import { Wrapper } from "../../Wrapper";
import styles from "../styles/CheckoutTotalTable.module.scss";

interface Props {
  localProjectAddon: LocalProjectAddon | null;
  numLocalTrees: number;
  selectedPlanAddons?: FamilyPlanOptions | BusinessPlanOptions;
  selectedPlanId: string | null;
  location: Location | null;
}

export const CheckoutTotalTable: React.FC<Props> = (props) => {
  const {
    localProjectAddon,
    numLocalTrees,
    selectedPlanAddons,
    selectedPlanId,
    location,
  } = props;

  if (!selectedPlanId) {
    return null;
  }

  const {
    businessAddonsForLocation,
    businessPlanIdForLocation,
    currencySymbol,
    familyAddonsForLocation,
    familyPlanIdForLocation,
    plansForLocation,
  } = getLocationPlans(location);

  const isFamilyPlan = selectedPlanId === familyPlanIdForLocation;
  const isBusinessPlan = selectedPlanId === businessPlanIdForLocation;
  const selectedPlan = isFamilyPlan
    ? {
        price: parseFloat(
          calculateFamilyPlanCost(
            familyAddonsForLocation,
            selectedPlanAddons as FamilyPlanOptions
          )
        ),
        title: "Family",
      }
    : isBusinessPlan
    ? {
        price: parseFloat(
          calculateBusinessPlanCost(
            businessAddonsForLocation,
            selectedPlanAddons as BusinessPlanOptions
          )
        ),
        title: "Climate Action Workforce",
      }
    : plansForLocation[selectedPlanId];

  if (!selectedPlan) {
    return null;
  }

  const planTotal = selectedPlan.price;
  const localAddonTotal =
    localProjectAddon && numLocalTrees
      ? numLocalTrees * localProjectAddon.subscriptionPrice
      : 0;
  const total = planTotal + localAddonTotal;

  // Is it a family plan without any local project addons available
  const isNormalFamilyPlan = isFamilyPlan && !localProjectAddon;

  return (
    <section className={styles.section} data-is-cream-bg={isNormalFamilyPlan}>
      <Wrapper isSmall>
        <div>
          {!isNormalFamilyPlan && (
            <div
              className={styles.tableRow}
              data-testid="checkout-table-sub-cost"
            >
              <div className={styles.tableRowColumn}>
                {selectedPlan.title} plan
              </div>
              <div className={styles.tableRowColumn}>
                {currencySymbol}
                {toCurrency(planTotal)}
              </div>
            </div>
          )}
          {numLocalTrees > 0 && (
            <div
              className={styles.tableRow}
              data-testid="checkout-table-trees-cost"
            >
              <div className={styles.tableRowColumn}>
                {`${pluralise(numLocalTrees, "x local tree")} funded`}
              </div>
              <div className={styles.tableRowColumn}>
                {currencySymbol}
                {toCurrency(localAddonTotal)}
              </div>
            </div>
          )}
        </div>
        <div
          className={styles.tableRowTotal}
          data-testid="checkout-table-total-cost"
        >
          <div className={styles.tableRowColumn}>Monthly total</div>
          <div className={styles.tableRowColumn}>
            {currencySymbol === "$" ? "US$" : currencySymbol}
            {toCurrency(total)}
          </div>
        </div>
      </Wrapper>
    </section>
  );
};
